
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { subscriptionPlans, SubscriptionPlan } from '@/types/subscriptionPlans';
import { upgradeToPaidPlan } from '@/utils/subscriptionService';

const STORAGE_KEY = 'admin_subscription_plans';

// Update the PlanType to include yearly
export type PlanType = 'monthly' | 'quarterly' | 'yearly';

export const useAdminPlans = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [activeTab, setActiveTab] = useState('preview'); // Set default to preview tab
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>('monthly');
  const [showCheckout, setShowCheckout] = useState(false);
  
  const fromSubscription = location.state?.fromSubscription || false;
  const isTrialUser = location.state?.isTrialUser || false;

  useEffect(() => {
    const loadPlans = () => {
      const savedPlans = localStorage.getItem(STORAGE_KEY);
      if (savedPlans) {
        try {
          setPlans(JSON.parse(savedPlans));
        } catch (error) {
          console.error('Error parsing saved plans:', error);
          setPlans(subscriptionPlans);
        }
      } else {
        const superAdminPlans = localStorage.getItem('superadmin_subscription_plans');
        if (superAdminPlans) {
          try {
            setPlans(JSON.parse(superAdminPlans));
          } catch (error) {
            console.error('Error parsing super admin plans:', error);
            setPlans(subscriptionPlans);
          }
        } else {
          setPlans(subscriptionPlans);
        }
      }
    };

    loadPlans();
    
    // If coming from subscription page, set appropriate tab and plan type
    if (fromSubscription) {
      setActiveTab('preview');
      
      // If we have a recommended plan, select it by default
      const recommendedPlan = subscriptionPlans.find(plan => plan.recommended);
      if (recommendedPlan) {
        setSelectedPlanId(recommendedPlan.id);
      }
    }
  }, [fromSubscription]);

  const handleUpdatePlan = (index: number, field: keyof SubscriptionPlan, value: any) => {
    const updatedPlans = [...plans];
    updatedPlans[index] = {
      ...updatedPlans[index],
      [field]: value
    };
    setPlans(updatedPlans);
  };

  const handleSavePlans = async () => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({
        title: "Plans Saved",
        description: "Subscription plans have been updated successfully",
      });
    } catch (error) {
      console.error('Error saving plans:', error);
      toast({
        title: "Error Saving Plans",
        description: "There was a problem saving the subscription plans",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addNewPlan = () => {
    const newPlan: SubscriptionPlan = {
      id: `plan-${Date.now()}`,
      name: "New Plan",
      monthlyPrice: 49,
      quarterlyPrice: 125,
      yearlyPrice: 470,
      quarterlyDiscount: 15,
      yearlyDiscount: 25,
      description: "New subscription plan",
      features: [
        { name: 'Custom domain', included: true },
        { name: 'White-labeled experience', included: true },
        { name: 'Email notifications', included: true },
      ],
      maxReferrals: 100,
      maxManagers: 3,
      maxReferralLinks: 10
    };
    
    setPlans([...plans, newPlan]);
  };

  const deletePlan = (index: number) => {
    const updatedPlans = plans.filter((_, i) => i !== index);
    setPlans(updatedPlans);
  };

  const handleSelectPlan = (planId: string, planType: PlanType) => {
    setSelectedPlanId(planId);
    setSelectedPlanType(planType);
  };

  const handlePurchasePlan = () => {
    if (!selectedPlanId) return;
    
    if (isTrialUser) {
      setShowCheckout(true);
    } else {
      processPayment();
    }
  };

  const processPayment = async (formData?: any) => {
    if (!selectedPlanId) return;
    
    setIsProcessing(true);
    try {
      console.log('Processing payment with form data:', formData);
      
      // Convert quarterly/yearly to the right format
      const billingCycle = selectedPlanType as 'monthly' | 'quarterly' | 'yearly';
      
      const result = await upgradeToPaidPlan(selectedPlanId, billingCycle);
      
      if (result.success) {
        toast({
          title: "Subscription Activated",
          description: result.message || `Successfully upgraded to the ${selectedPlanId} plan.`,
        });
        
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else {
        toast({
          title: "Subscription Failed",
          description: result.message || "There was a problem processing your subscription.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error purchasing plan:', error);
      toast({
        title: "Error",
        description: "There was a problem with your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
  };

  const getSelectedPlan = (): SubscriptionPlan | null => {
    if (!selectedPlanId) return null;
    return plans.find(plan => plan.id === selectedPlanId) || null;
  };

  return {
    plans,
    isSaving,
    activeTab,
    isProcessing,
    selectedPlanId,
    selectedPlanType,
    fromSubscription,
    isTrialUser,
    showCheckout,
    setActiveTab,
    handleUpdatePlan,
    handleSavePlans,
    addNewPlan,
    deletePlan,
    handleSelectPlan,
    handlePurchasePlan,
    processPayment,
    handleBackToPlans,
    getSelectedPlan
  };
};
